import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import type { Order } from '@/types/orders';
import { describeEspejoDropi, formatNumber } from './orders-format';
import {
  useDropiPrevisualizacion,
  useDropiStatus,
  useEnviarOrdenADropi,
  useSincronizarOrdenDropi,
} from '../integrations/use-dropi';

/**
 * Enviar una orden del panel a Dropi (docs/DROPI.md).
 *
 * El flujo acordado con el negocio: la orden nace y se edita ACÁ y solo se manda a Dropi cuando el
 * admin la da por buena; en Dropi queda `PENDIENTE CONFIRMACION` y se confirma a mano allá.
 *
 * Antes de enviar se muestra la PREVISUALIZACIÓN (endpoint del backend), que es donde se ve lo que
 * de verdad importa:
 *  - la ubicación ya traducida al catálogo de Dropi («Valle Del Cauca» → `VALLE`), con el aviso de
 *    cómo se corrigió;
 *  - los productos que faltan vincular (el envío no se puede hacer hasta resolverlos);
 *  - el motivo exacto cuando algo no cuadra, en vez del «La ciudad no existe…» de Dropi.
 *
 * Es IDEMPOTENTE: si la orden ya está en Dropi el botón es «Actualizar» (`PUT`), así que corregir la
 * dirección y volver a enviar no crea una segunda orden.
 */
export interface OrderDropiDialogProps {
  open: boolean;
  onClose: () => void;
  /** Orden a enviar (`null` = diálogo cerrado). */
  order: Order | null;
}

export function OrderDropiDialog({ open, onClose, order }: OrderDropiDialogProps) {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const status = useDropiStatus(tenantId);
  const enviar = useEnviarOrdenADropi(tenantId, order?.id ?? null);
  const sincronizar = useSincronizarOrdenDropi(tenantId, order?.id ?? null);
  const preview = useDropiPrevisualizacion(tenantId, order?.id ?? null, open);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    enviar.reset();
    sincronizar.reset();
  }, [open, order?.id]);

  const conectado = Boolean(status.data?.configurada && status.data?.activo);
  const yaEnviada = Boolean(order?.dropiOrderId);
  const ubicacion = preview.data?.ubicacion ?? null;

  async function handleEnviar(reenviar: boolean) {
    setError(null);
    await enviar.mutateAsync({ reenviar }).catch((e: unknown) => setError(getErrorMessage(e)));
  }

  async function handleSincronizar() {
    setError(null);
    await sincronizar.mutateAsync().catch((e: unknown) => setError(getErrorMessage(e)));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enviar a Dropi"
      description={order ? `${order.clienteNombre} · ${formatNumber(order.total)}` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={enviar.isPending}>
            Cerrar
          </Button>

          {yaEnviada ? (
            <Button
              variant="secondary"
              loading={sincronizar.isPending}
              disabled={!canEdit}
              onClick={() => void handleSincronizar()}
            >
              Actualizar estado
            </Button>
          ) : null}

          <Button
            loading={enviar.isPending}
            disabled={!canEdit || !conectado || (!yaEnviada && preview.data?.listo !== true)}
            onClick={() => void handleEnviar(yaEnviada)}
          >
            {yaEnviada ? 'Actualizar en Dropi' : 'Enviar a Dropi'}
          </Button>
        </>
      }
    >
      {!conectado ? (
        <Alert tone="warning">
          Este negocio no tiene Dropi conectado. Conectalo en Integraciones → Dropshipping.
        </Alert>
      ) : (
        <div className="space-y-4">
          {yaEnviada ? (
            <div className="flex flex-wrap items-center gap-2 rounded-control border border-line bg-surface-muted px-3 py-2 text-caption text-ink-muted">
              <Badge tone="success" dot>
                En Dropi
              </Badge>
              <span>#{order?.dropiOrderId}</span>
              {order?.dropiEstado ? <span>· {order.dropiEstado}</span> : null}
              {order?.dropiFlete ? <span>· flete {formatNumber(order.dropiFlete)}</span> : null}
              {order?.dropiTransportadora ? <span>· {order.dropiTransportadora}</span> : null}
            </div>
          ) : null}

          {preview.isPending ? (
            <div className="flex items-center justify-center gap-2 py-6 text-caption text-ink-muted">
              <Spinner /> Armando la orden para Dropi…
            </div>
          ) : null}

          {preview.data ? (
            <>
              {preview.data.problema ? (
                <Alert tone="warning">{preview.data.problema}</Alert>
              ) : (
                <Alert tone="success">Todo listo para crear la orden en Dropi.</Alert>
              )}

              {preview.data.avisoBodega ? (
                <Alert tone="warning">{preview.data.avisoBodega}</Alert>
              ) : null}

              {/* Cómo se cobra (docs/DROPI.md): se deriva del método de pago de la orden. Antes estaba
                  fijo en contraentrega, así que una tienda que cobra anticipado le cobraba dos veces
                  al cliente. */}
              {preview.data.avisoRecaudo ? (
                <Alert tone="warning">{preview.data.avisoRecaudo}</Alert>
              ) : null}

              {preview.data.recaudo ? (
                <div className="space-y-1 text-caption">
                  <p className="font-medium text-ink">Cómo se cobra</p>
                  <p className="text-ink-muted">
                    {preview.data.recaudo === 'SIN RECAUDO'
                      ? 'SIN RECAUDO: el cliente ya pagó (método de pago anticipado), así que el proveedor no le cobra al entregar.'
                      : 'CON RECAUDO (contraentrega): el proveedor cobra al entregar.'}
                  </p>
                  <p className="text-micro text-ink-faint">
                    Sale del método de pago de la orden: si está mal, corregí ese dato en la orden.
                  </p>
                </div>
              ) : null}

              {ubicacion ? (
                <div className="space-y-1 text-caption">
                  <p className="font-medium text-ink">Ubicación que recibirá Dropi</p>
                  <p className="text-ink-muted">
                    {ubicacion.ciudad && ubicacion.departamento
                      ? `${ubicacion.ciudad}, ${ubicacion.departamento}`
                      : 'Sin resolver'}
                  </p>
                  {ubicacion.aviso ? <p className="text-ink-faint">{ubicacion.aviso}</p> : null}
                </div>
              ) : null}

              {/* De dónde SALE el pedido: Dropi valida la ciudad de la bodega del proveedor, así que
                  verla antes de enviar evita el error «no se encontró la ciudad de operación». */}
              {preview.data.bodegas.length > 0 ? (
                <div className="space-y-1 text-caption">
                  <p className="font-medium text-ink">Sale de la bodega del proveedor</p>
                  <ul className="space-y-0.5">
                    {preview.data.bodegas.map((b) => (
                      <li
                        key={b.producto}
                        className="flex flex-wrap items-center gap-x-2 text-ink-muted"
                      >
                        <span>
                          {b.producto}: {b.bodega ? `${b.bodega} · ` : ''}
                          {b.ciudad ?? 'ciudad sin dato'}
                          {b.codDane ? ` (${b.codDane})` : ''}
                        </span>
                        {b.sinCobertura ? <Badge tone="warning">Sin cobertura</Badge> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {preview.data.payload ? (
                <div className="space-y-1 text-caption">
                  <p className="font-medium text-ink">Detalle del envío</p>
                  <pre className="max-h-56 overflow-auto rounded-control bg-surface-muted p-2 text-micro text-ink-muted">
                    {JSON.stringify(preview.data.payload, null, 2)}
                  </pre>
                </div>
              ) : null}
            </>
          ) : null}

          {preview.isError ? <Alert tone="warning">{getErrorMessage(preview.error)}</Alert> : null}
          {error ? <Alert tone="warning">{error}</Alert> : null}
          {enviar.data ? (
            <Alert tone="success">
              {enviar.data.yaExistia
                ? `Orden actualizada en Dropi (#${enviar.data.dropiOrderId}).`
                : `Orden creada en Dropi (#${enviar.data.dropiOrderId}): queda pendiente de confirmación allá.`}
            </Alert>
          ) : null}
          {sincronizar.data ? (
            <Alert tone="success">{describeEspejoDropi(sincronizar.data)}</Alert>
          ) : null}
        </div>
      )}
    </Modal>
  );
}